/**
 * Review-of-merge items (board s1389-1 (b); contract note
 * `docs/specs/incoming/XC-ERP1-REVIEW-OF-MERGE-CARD_Frontend_Contract_Note_v0_1.md`):
 * `GET /api/v1/entity-reviews/merge-reviews` (gateway → entity_resolution `/reviews/merge-reviews`).
 *
 * When Wingo combined two records before Dub answered the card that asked about them, the engine
 * withdraws that card and asks about the merge instead: "We combined these two. Keep them combined or
 * undo?". `items` are the open ones, `total` all of them (AW-51); `recent` the ones kept or undone in
 * the last week, a keep with its Undo.
 *
 * The reply is held to this schema before the Inbox draws it: a merge_review item off the contract
 * refuses the whole reply (never drawn in part). An item of another `kind` is skipped, so the feed can
 * grow kinds without blanking the Inbox; objects are not strict for the same reason. Ids are for keys
 * and calls only, never printed. The shape both sides hold to is pinned in
 * `__fixtures__/mergeReviews.v1.json` (the engine's route test regenerates it).
 */

import { z } from 'zod';

export const MERGE_REVIEW_KIND = 'merge_review';

const isoInstant = z.string().datetime({ offset: true });
/** A display name: never empty, never a bare UUID (the engine mints no item without both names). */
const recordName = z
  .string()
  .trim()
  .min(1)
  .refine((s) => !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s), {
    message: 'a record name must not be an id',
  });

export const MergeReviewRecordSchema = z.object({
  entity_id: z.string().uuid(),
  name: recordName,
});

/**
 * `undone`: a person's "separate" that actually separated the two records (the lane's unmake ran).
 * `changed`: the merge was reverted or invalidated any other way (an override the lane only recorded, a
 * delta re-resolve, a sweep): nobody can say the records were separated, so the card never claims it.
 */
export const MERGE_REVIEW_STATUSES = ['pending', 'kept', 'undone', 'changed'] as const;

export const MergeReviewSchema = z.object({
  id: z.string().uuid(),
  kind: z.literal(MERGE_REVIEW_KIND),
  status: z.enum(MERGE_REVIEW_STATUSES),
  entity_type: z.string().min(1),
  /** The record the merge kept (the merge's b side, by its live tip's name). */
  kept_record: MergeReviewRecordSchema,
  /** The record combined into it (the merge's a side, closed by the merge). */
  merged_record: MergeReviewRecordSchema,
  /** The uniting merge: what Undo reverts through the existing revert lane. */
  merge_decision_id: z.string().uuid(),
  merged_at: isoInstant,
  /** The merge row's `decided_by`, verbatim (llm, cascade, system, learned_pattern, human). */
  merged_by: z.string().min(1),
  /** The merge's own reasoning in one plain sentence, when it has one. */
  reason_line: z.string().nullable().optional(),
  can_undo: z.boolean(),
  /**
   * Why Undo is unavailable, as a CODE the frontend words (never server prose: the revert lane's own
   * refusals name internal commands): `window_expired`, `lane_owned_fold`, `already_reverted`,
   * `not_unmakeable` (the lane would only record it, not separate the records); an unknown code reads
   * as a plain "can't be undone here".
   */
  undo_unavailable: z.string().nullable().optional(),
  asked_at: isoInstant,
  reviewed_at: isoInstant.nullable().optional(),
  can_undo_keep: z.boolean().default(false),
});

/** One element of a list: a merge_review held to the schema, any other kind skipped (`null`). */
const FeedEntrySchema = z
  .object({ kind: z.string() })
  .passthrough()
  .transform((entry, ctx) => {
    if (entry.kind !== MERGE_REVIEW_KIND) return null;
    const parsed = MergeReviewSchema.safeParse(entry);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) ctx.addIssue(issue);
      return z.NEVER;
    }
    return parsed.data;
  });

const feedList = z
  .array(FeedEntrySchema)
  .transform((entries) => entries.filter((e): e is MergeReview => e !== null));

export const MergeReviewFeedSchema = z.object({
  items: feedList,
  total: z.number().int().nonnegative(),
  page: z.number().int().positive().default(1),
  page_size: z.number().int().positive().default(20),
  has_next: z.boolean().default(false),
  recent: feedList.default([]),
  /** Reviews the caller's role may not see, left out and counted (XC-WHO-SEES-WHAT W1q-C2). */
  hidden_for_role: z.number().int().nonnegative().default(0),
});

export const RevertResultSchema = z.object({
  revert_decision_id: z.string().uuid(),
  predecessor_decision_id: z.string().uuid(),
});

export type MergeReviewRecord = z.infer<typeof MergeReviewRecordSchema>;
export type MergeReviewStatus = (typeof MERGE_REVIEW_STATUSES)[number];
export type MergeReview = z.infer<typeof MergeReviewSchema>;
export type MergeReviewFeed = z.infer<typeof MergeReviewFeedSchema>;

/** The feed before the engine serves it, or with nothing to review. */
export const EMPTY_MERGE_REVIEW_FEED: MergeReviewFeed = {
  items: [],
  total: 0,
  page: 1,
  page_size: 20,
  has_next: false,
  recent: [],
  hidden_for_role: 0,
};
